import React, { useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Notification } from './Notification';
import { useNotificationAnimation } from './useNotificationAnimation';
import { notificationsContext } from './notificationsContext';
import './NotificationsStack.css';

/**
 * Fixed top-center stacked host for app-wide toast notifications.
 *
 * Ported from the legacy `NotificationsStack.tsx`: renders into
 * `document.body` via `ReactDOM.createPortal`, positions each toast
 * absolutely with a `--translate` CSS var computed from the tracked heights
 * (`useNotificationAnimation`), so toasts stack downward from the top.
 *
 * Rendered exactly once in `App.js` inside the `NotificationsProvider`.
 */
export const NotificationsStack = () => {
    const context = useContext(notificationsContext);
    const listRef = useRef(null);
    const getOffsetFromIndex = useNotificationAnimation(listRef);

    if (context === undefined) {
        return null;
    }

    const { stack, removeNotification } = context;

    if (document) {
        return ReactDOM.createPortal(
            <div className="NotificationsStack">
                <ul className="NotificationStackContainer" ref={listRef}>
                    {stack
                        .slice()
                        .reverse()
                        .map((notification, index) => (
                            <li
                                key={notification.id}
                                className="NotificationToast"
                                data-index={index}
                                style={{
                                    '--translate': `${getOffsetFromIndex(index)}px`,
                                }}
                            >
                                <Notification
                                    notification={notification}
                                    removeNotification={removeNotification}
                                />
                            </li>
                        ))}
                </ul>
            </div>,
            document.body
        );
    }

    return null;
};

export default NotificationsStack;